import React, { useState, useRef, useMemo } from 'react'

import { CheckIcon, ChevronsUpDown, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { Country } from '@/core/api/entities/misc/country'
import { useCountries } from '@/core/hooks/misc/useCountries'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { Button } from '@/shared/components/atoms/Button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/shared/components/atoms/Command'
import { Input } from '@/shared/components/atoms/Input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { cn } from '@/shared/utils/helpers'

type CountrySelectorProps = {
	value?: string
	onChange: (country: string) => void
	placeholder?: string
	disabled?: boolean
	className?: string
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
	value: selectedCountry,
	onChange,
	placeholder = 'Select country...',
	disabled = false,
	className,
}) => {
	const { t } = useTranslation('common')

	const scrollAreaRef = useRef<HTMLDivElement>(null)
	const [searchValue, setSearchValue] = useState('')
	const [isOpen, setIsOpen] = useState(false)

	// Debounce search value with 300ms delay
	const debouncedSearchValue = useDebounce(searchValue, 300)

	// Fetch countries from API
	const { data: countries = [], isLoading, isError } = useCountries()

	// Find selected country object
	const selectedCountryObj = countries.find(
		(country) => country.iso === selectedCountry
	)

	const handleSelect = (country: Country) => {
		onChange(country.iso)
		setIsOpen(false)
		setSearchValue('')
	}

	// Filter countries based on debounced search value
	const filteredCountries = useMemo(() => {
		// If search value is less than 2 characters, show all countries
		if (!debouncedSearchValue || debouncedSearchValue.length < 2) {
			return countries
		}

		return countries.filter(
			(country) =>
				country.name
					.toLowerCase()
					.includes(debouncedSearchValue.toLowerCase()) ||
				country.iso.toLowerCase().includes(debouncedSearchValue.toLowerCase())
		)
	}, [countries, debouncedSearchValue])

	// Show search hint when user types but less than 2 characters
	const showSearchHint = searchValue.length > 0 && searchValue.length < 2

	if (isError) {
		return (
			<div className="flex items-center justify-center h-12 px-3 text-sm text-destructive bg-card border border-destructive rounded-lg">
				{t('common.error_loading_countries')}
			</div>
		)
	}

	return (
		<Popover
			open={isOpen}
			modal
			onOpenChange={(open) => {
				setIsOpen(open)
				if (!open) {
					setSearchValue('')
				}
			}}
		>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					className={cn(
						'flex justify-between items-center w-full h-9 px-3 text-left font-normal bg-transparent dark:bg-input/30',
						!selectedCountry && 'text-muted-foreground',
						className
					)}
					disabled={disabled || isLoading}
				>
					<div className="flex items-center gap-2">
						{selectedCountryObj && (
							<>
								<FlagComponent
									country={selectedCountryObj.iso as RPNInput.Country}
									countryName={selectedCountryObj.name}
								/>
								<span className="text-sm font-medium">
									{selectedCountryObj.name}
								</span>
							</>
						)}
						{!selectedCountryObj && (
							<span className="text-muted-foreground">
								{isLoading ? t('common.loading') : placeholder}
							</span>
						)}
					</div>
					<ChevronsUpDown
						className={cn(
							'size-4 opacity-50',
							disabled || isLoading ? 'hidden' : 'opacity-100'
						)}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px] p-0" align="start">
				<div className="p-2 border-b border-border">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground size-4" />
						<Input
							value={searchValue}
							onChange={(e) => {
								setSearchValue(e.target.value)
								setTimeout(() => {
									if (scrollAreaRef.current) {
										const viewportElement = scrollAreaRef.current.querySelector(
											'[data-radix-scroll-area-viewport]'
										)
										if (viewportElement) {
											viewportElement.scrollTop = 0
										}
									}
								}, 0)
							}}
							placeholder={t('account.user_profile.select_country')}
							className="pl-10"
						/>
					</div>
					{showSearchHint && (
						<p className="text-xs text-muted-foreground mt-1">
							{t('account.user_profile.type_at_least_2_characters_to_search')}
						</p>
					)}
				</div>
				<Command>
					<CommandList>
						<ScrollArea ref={scrollAreaRef} className="h-72">
							<CommandEmpty>
								{showSearchHint ? (
									<div className="text-center py-6">
										<p className="text-sm text-muted-foreground">
											{t(
												'account.user_profile.type_at_least_2_characters_to_search'
											)}
										</p>
									</div>
								) : (
									t('common.no_country_found')
								)}
							</CommandEmpty>
							<CommandGroup>
								{filteredCountries.map((country) => (
									<CommandItem
										key={country.iso}
										className="gap-2 cursor-pointer"
										onSelect={() => handleSelect(country)}
									>
										<FlagComponent
											country={country.iso as RPNInput.Country}
											countryName={country.name}
										/>
										<span className="flex-1 text-sm">{country.name}</span>
										<CheckIcon
											className={`ml-auto size-4 ${
												country.iso === selectedCountry
													? 'opacity-100'
													: 'opacity-0'
											}`}
										/>
									</CommandItem>
								))}
							</CommandGroup>
						</ScrollArea>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

const FlagComponent: React.FC<{
	country: RPNInput.Country
	countryName?: string
}> = ({ country, countryName }) => {
	const Flag = flags[country]

	return (
		<span className="flex h-4 w-6 overflow-hidden bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
			{Flag && <Flag title={countryName ?? ''} />}
		</span>
	)
}

export { CountrySelector }
